import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "../firebase/firebase";
// Updated imports to support querying by a specific field
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth"; 
import {
  LayoutDashboard, Package, FileText, Users, Database,
  Wallet, UserCheck, Settings, LogOut, Store, ShieldAlert
} from "lucide-react";

export default function StoreSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [storeMeta, setStoreMeta] = useState(null);
  const [userUid, setUserUid] = useState(localStorage.getItem("uid") || null);

  // 1. Fetch User UID dynamically using Firebase Auth
  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserUid(user.uid);
        localStorage.setItem("uid", user.uid); // Keep a local backup
      } else {
        setUserUid(null);
        setStoreMeta(null);
        localStorage.removeItem("uid");
        localStorage.removeItem("storeName");
        localStorage.removeItem("storeId");
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // 2. Fetch Store Data based on the 'linkedAuthUid' field
  useEffect(() => {
    if (!userUid) return;
    
    // Create a query against the collection where linkedAuthUid matches the logged-in user
    const storeQuery = query(
      collection(db, "stores"), 
      where("linkedAuthUid", "==", userUid)
    );
    
    const unsubscribe = onSnapshot(storeQuery, (querySnapshot) => {
      if (!querySnapshot.empty) {
        // Assuming one user is linked to one store, take the first document found
        const data = querySnapshot.docs[0].data();
        setStoreMeta(data);
        
        // Save storeName and storeId to localStorage for other components (like Barcode) to use
        if (data.storeName) localStorage.setItem("storeName", data.storeName);
        if (data.storeId) localStorage.setItem("storeId", data.storeId);
      } else {
        console.warn("Sidebar tracking failed: No store found linked to this UID");
        setStoreMeta(null);
      }
    }, (err) => console.error("Sidebar tracking failed: ", err));

    return () => unsubscribe();
  }, [userUid]);

  const menus = [
    { name: "Dashboard", path: "/store/dashboard", icon: LayoutDashboard },
    { name: "Inventory", path: "/store/inventory", icon: Package },
    { name: "Sales Terminal", path: "/store/sales", icon: FileText },
    { name: "Closing Stock", path: "/store/closing-stock", icon: FileText },

    { name: "Eye Testing Report", path: "/store/eye-testing", icon: Database },
    { name: "Customer CRM", path: "/store/customers", icon: Users },
    { name: "Expenses Log", path: "/store/expenses", icon: Wallet },
    // { name: "Employees Matrix", path: "/store/employees", icon: UserCheck },
  ];

  return (
    <aside className="hidden lg:flex flex-col w-72 h-screen bg-white border-r border-slate-200 sticky top-0 shadow-sm">

      {/* HEADER - CLEAN WHITE THEME */}
      <div className="p-6 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-4">
          <div className="bg-blue-600 p-3 rounded-2xl shadow-md">
            <Store size={22} className="text-white" />
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="font-black text-xs uppercase tracking-widest text-slate-900 truncate">
              {storeMeta?.storeName || "Store Terminal"}
            </h2>
            <p className="text-[10px] font-bold text-blue-600 truncate mt-1 bg-blue-50 px-2 py-0.5 rounded-lg w-max uppercase tracking-wider">
              ID: {storeMeta?.storeId || "LOADING..."}
            </p>
          </div>
        </div>
      </div>

      {/* MENU LINKS - LIGHT THEME */}
      <div className="flex-1 p-5 space-y-2 overflow-y-auto">
        {menus.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.path;

          return (
            <Link key={item.path} to={item.path}>
              <motion.div
                whileHover={{ x: 4 }}
                className={`flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-200 ${
                  active
                    ? "bg-blue-600 text-white font-bold shadow-md shadow-blue-200"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <Icon size={18} />
                <span className="text-sm font-bold tracking-wide">{item.name}</span>
              </motion.div>
            </Link>
          );
        })}
      </div>

      {/* FOOTER BLOCK */}
      <div className="p-5 border-t border-slate-100 space-y-4">
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex items-start gap-3">
          <ShieldAlert size={20} className="text-blue-500 shrink-0 mt-0.5" />
          <div className="text-[10px] text-slate-600 leading-snug">
            <strong className="text-slate-900 block mb-0.5">Secure Session</strong>
            Branch access protected and verified.
          </div>
        </div>

        <button
          onClick={() => { 
            const auth = getAuth();
            auth.signOut().then(() => {
              localStorage.clear(); 
              navigate("/"); 
            });
          }}
          className="w-full flex items-center justify-center gap-3 bg-white hover:bg-rose-50 border border-slate-200 hover:border-rose-200 p-4 rounded-2xl text-[10px] font-black text-slate-600 hover:text-rose-600 transition-all active:scale-95 shadow-sm"
        >
          <LogOut size={14} />
          LOGOUT
        </button>
      </div>
    </aside>
  );
}